import {
  CalendarOutlined,
  EllipsisOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, DatePicker, Dropdown, Input, Radio, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useLazyGetCustomersQuery } from '@/modules/customers/customersApi';
import { useGetOrdersQuery } from '@/modules/orders/ordersApi';
import type {
  Order,
  OrderStatus,
  OrderStatusFilter,
  PaymentMethod,
  PaymentMethodFilter,
  PaymentStatus,
  PaymentStatusFilter,
} from '@/types/order';
import {
  ORDER_STATUS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
} from '@/types/order';
import { formatOrderMoney } from '@/utils/currency';
import { formatDateTime } from '@/utils/date';
import {
  getFulfillmentKindColor,
  getFulfillmentKindLabel,
  type OrderFulfillmentKind,
} from '@/utils/orderFulfillment';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useOrderQueryParams } from './hooks/useOrderQueryParams';
import styles from './Orders.module.scss';

function orderStatusTag(status: OrderStatus) {
  const colors: Record<OrderStatus, string> = {
    pending: 'gold',
    confirmed: 'blue',
    processing: 'cyan',
    packed: 'purple',
    creating: 'orange',
    shipped: 'geekblue',
    delivered: 'success',
    installed: 'lime',
    closed: 'green',
    cancelled: 'error',
    returned: 'default',
  };

  const label =
    ORDER_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? ucfirst(status);
  return <Tag color={colors[status]}>{label}</Tag>;
}

function paymentStatusTag(status: PaymentStatus) {
  const colors: Record<PaymentStatus, string> = {
    pending: 'gold',
    paid: 'success',
    failed: 'error',
    refunded: 'default',
  };

  const label =
    PAYMENT_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? ucfirst(status);
  return <Tag color={colors[status]}>{label}</Tag>;
}

function fulfillmentKindTag(kind: OrderFulfillmentKind = 'print') {
  return <Tag color={getFulfillmentKindColor(kind)}>{getFulfillmentKindLabel(kind)}</Tag>;
}

function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_METHOD_OPTIONS.find((option) => option.value === method)?.label ?? ucfirst(method);
}

export default function Orders() {
  const navigate = useNavigate();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useOrderQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetOrdersQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const orders = useMemo(() => listResult?.results ?? [], [listResult?.results]);
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [customerDraft, setCustomerDraft] = useState<string | null>(state.customerId || null);
  const [orderStatusDraft, setOrderStatusDraft] = useState<OrderStatusFilter>(state.orderStatus);
  const [paymentStatusDraft, setPaymentStatusDraft] = useState<PaymentStatusFilter>(
    state.paymentStatus,
  );
  const [paymentMethodDraft, setPaymentMethodDraft] = useState<PaymentMethodFilter>(
    state.paymentMethod,
  );
  const [placedDraft, setPlacedDraft] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    state.dateFrom || state.dateTo
      ? [state.dateFrom ? dayjs(state.dateFrom) : null, state.dateTo ? dayjs(state.dateTo) : null]
      : null,
  );

  const [fetchCustomers, { data: customersResult, isFetching: customersLoading }] =
    useLazyGetCustomersQuery();

  const customerFilterOptions = useMemo(() => {
    const customers = customersResult?.results ?? [];
    const options = customers.map((customer) => ({
      label: `${customer.name} (${customer.email})`,
      value: customer.id,
    }));

    if (state.customerId && !options.some((option) => option.value === state.customerId)) {
      const order = orders.find((item) => item.customerId === state.customerId);
      if (order) {
        options.unshift({
          label: order.customerPhone
            ? `${order.customerName} (${order.customerPhone})`
            : order.customerName,
          value: state.customerId,
        });
      }
    }

    return options;
  }, [customersResult?.results, orders, state.customerId]);

  const loadCustomerFilterOptions = useCallback(() => {
    void fetchCustomers({
      page: 1,
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc',
      status: 'active',
    });
  }, [fetchCustomers]);

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setCustomerDraft(null);
    setOrderStatusDraft('all');
    setPaymentStatusDraft('all');
    setPaymentMethodDraft('all');
    setPlacedDraft(null);
    resetAll();
  };

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const handleTableChange: TableProps<Order>['onChange'] = (pagination) => {
    const pageChanged = pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged = pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (order: Order): MenuProps['items'] => [
      {
        key: 'view',
        label: 'View order',
        onClick: () => navigate(`/orders/${order.id}`),
      },
    ],
    [navigate],
  );

  const columns: ColumnsType<Order> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="ORDER #"
            field="orderNumber"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.search ? [state.search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Order # or coupon"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(state.search);
          },
        },
        render: (orderNumber: string, record) => (
          <Link to={`/orders/${record.id}`} className={styles.orderNumberCell}>
            {orderNumber}
          </Link>
        ),
      },
      {
        title: 'FORMAT',
        dataIndex: 'fulfillmentKind',
        key: 'fulfillmentKind',
        width: 120,
        render: (kind: OrderFulfillmentKind | undefined) => fulfillmentKindTag(kind),
      },
      {
        title: 'CUSTOMER',
        key: 'customer',
        className: styles.customerCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.customerId ? [state.customerId] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ customerId: customerDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setCustomerDraft(null);
              patchParams({ customerId: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              allowClear
              showSearch
              size="small"
              placeholder="All customers"
              value={customerDraft ?? undefined}
              onChange={(value) => setCustomerDraft(value ?? null)}
              loading={customersLoading}
              options={customerFilterOptions}
              optionFilterProp="label"
              className={styles.filterSelect}
              onOpenChange={(visible) => {
                if (visible) loadCustomerFilterOptions();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setCustomerDraft(state.customerId || null);
              loadCustomerFilterOptions();
            }
          },
        },
        render: (_value, record) => (
          <div className={styles.customerBlock}>
            <Link to={`/customers/${record.customerId}`} className={styles.customerName}>
              {record.customerName || '—'}
            </Link>
            <span className={styles.customerMeta}>
              {[record.customerPhone, record.customerEmail].filter(Boolean).join(' · ') || '—'}
            </span>
          </div>
        ),
      },
      {
        title: 'ITEMS',
        key: 'items',
        render: (_value, record) => (
          <span className={styles.itemsCell}>
            {record.totalItems} item{record.totalItems === 1 ? '' : 's'} · {record.totalQuantity}{' '}
            qty
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="TOTAL"
            field="grandTotal"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'grandTotal',
        key: 'grandTotal',
        render: (value: number, record) => (
          <span className={styles.totalCell}>{formatOrderMoney(value, record.priceOrigin)}</span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="PAYMENT METHOD"
            field="paymentMethod"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'paymentMethod',
        key: 'paymentMethod',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.paymentMethod !== 'all' ? [state.paymentMethod] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { paymentMethod: paymentMethodDraft === 'all' ? null : paymentMethodDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setPaymentMethodDraft('all');
              patchParams({ paymentMethod: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              size="small"
              className={styles.filterSelect}
              value={paymentMethodDraft}
              onChange={setPaymentMethodDraft}
              options={[{ label: 'All methods', value: 'all' }, ...PAYMENT_METHOD_OPTIONS]}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setPaymentMethodDraft(state.paymentMethod);
          },
        },
        render: (method: PaymentMethod) => paymentMethodLabel(method),
      },
      {
        title: (
          <SortableColumnTitle
            label="PAYMENT STATUS"
            field="paymentStatus"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'paymentStatus',
        key: 'paymentStatus',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.paymentStatus !== 'all' ? [state.paymentStatus] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { paymentStatus: paymentStatusDraft === 'all' ? null : paymentStatusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setPaymentStatusDraft('all');
              patchParams({ paymentStatus: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={paymentStatusDraft}
              onChange={(event) => setPaymentStatusDraft(event.target.value)}
              className={styles.statusFilter}
            >
              <Radio value="all">All</Radio>
              {PAYMENT_STATUS_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setPaymentStatusDraft(state.paymentStatus);
          },
        },
        render: (status: PaymentStatus) => paymentStatusTag(status),
      },
      {
        title: (
          <SortableColumnTitle
            label="DELIVERY STATUS"
            field="orderStatus"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'orderStatus',
        key: 'orderStatus',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.orderStatus !== 'all' ? [state.orderStatus] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { orderStatus: orderStatusDraft === 'all' ? null : orderStatusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setOrderStatusDraft('all');
              patchParams({ orderStatus: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={orderStatusDraft}
              onChange={(event) => setOrderStatusDraft(event.target.value)}
              className={styles.statusFilter}
            >
              <Radio value="all">All</Radio>
              {ORDER_STATUS_OPTIONS.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setOrderStatusDraft(state.orderStatus);
          },
        },
        render: (status: OrderStatus) => orderStatusTag(status),
      },
      {
        title: 'COUPON',
        dataIndex: 'couponCode',
        key: 'couponCode',
        render: (value?: string) =>
          value ? <span className={styles.couponCell}>{value}</span> : '—',
      },
      {
        title: (
          <SortableColumnTitle
            label="PLACED"
            field="placedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'placedAt',
        key: 'placedAt',
        filterIcon: (filtered) => (
          <CalendarOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.dateFrom || state.dateTo ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                {
                  dateFrom: placedDraft?.[0]?.format('YYYY-MM-DD') || null,
                  dateTo: placedDraft?.[1]?.format('YYYY-MM-DD') || null,
                },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setPlacedDraft(null);
              patchParams({ dateFrom: null, dateTo: null }, { resetPage: true });
              close();
            }}
          >
            <DatePicker.RangePicker
              size="small"
              value={placedDraft}
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => setPlacedDraft(values)}
              style={{ width: '100%' }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setPlacedDraft(
                state.dateFrom || state.dateTo
                  ? [
                      state.dateFrom ? dayjs(state.dateFrom) : null,
                      state.dateTo ? dayjs(state.dateTo) : null,
                    ]
                  : null,
              );
            }
          },
        },
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button type="text" size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        ),
      },
    ],
    [
      activeSortBy,
      activeSortOrder,
      customerDraft,
      customerFilterOptions,
      customersLoading,
      getRowActionItems,
      handleSort,
      loadCustomerFilterOptions,
      orderStatusDraft,
      patchParams,
      paymentMethodDraft,
      paymentStatusDraft,
      placedDraft,
      searchDraft,
      state.dateFrom,
      state.dateTo,
      state.customerId,
      state.orderStatus,
      state.paymentMethod,
      state.paymentStatus,
      state.search,
    ],
  );

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          hasActiveFiltersOrSort ? (
            <Button
              type="default"
              size="small"
              className={styles.headerBtn}
              icon={<ReloadOutlined />}
              onClick={handleResetFiltersAndSort}
            >
              Reset
            </Button>
          ) : null
        }
      />

      <div className={styles.tableCard}>
        <Table<Order>
          className={styles.table}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={orders}
          loading={isLoading && !listResult}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (value) => `${value} orders`,
            size: 'small',
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
}
